import { getDeviceTimezone } from '@/utils/date';
import { transformToManagerRepRowData } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/transformers/manager-rep-summaries-transformers';
import { useGeneratedSalesPortalManagerRepSummariesQuery } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-manager-rep-summaries.generated';
import { useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import type {
  ManagerRepRowData,
  ManagerRepTimeRange,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';

interface UseManagerRepSummariesLoaderParams {
  businessOrganizationUUID: string;
  timeRange: ManagerRepTimeRange;
}

interface UseManagerRepSummariesLoaderResult {
  refetchReps: () => void;
  reps: ManagerRepRowData[];
  repsError?: ApolloError;
  repsLoading: boolean;
}

export function useManagerRepSummariesLoader({
  businessOrganizationUUID,
  timeRange,
}: UseManagerRepSummariesLoaderParams): UseManagerRepSummariesLoaderResult {
  const userTimezone = getDeviceTimezone();

  const {
    data: repData,
    error: repsError,
    loading: repsLoading,
    refetch,
  } = useGeneratedSalesPortalManagerRepSummariesQuery({
    notifyOnNetworkStatusChange: true,
    variables: { businessOrganizationUUID, userTimezone },
  });

  const reps = useMemo(
    () =>
      transformToManagerRepRowData(
        repData?.getSalesPortalManagerRepSummaries ?? [],
        timeRange,
      ),
    [repData, timeRange],
  );

  return {
    refetchReps: () => {
      refetch().catch(() => {});
    },
    reps,
    repsError,
    repsLoading,
  };
}
